import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonProperties } from '../common/common.interfaces';
import { useLoading } from '../loading/LoadingContext';
import { useUserContext } from '../user/UserContext';
import { CrystalBallRolesTableRow } from './admin.users.interfaces';
import { DateTime } from 'luxon';
import { apiFetch } from '../utility/fetch';
import CustomError, { CustomErrorProps } from '../common/components/CustomError';
import './admin.users.css'
import RefreshSvg from '../common/components/svg/RefreshSvg';
import { Accordion, Button, Col, Form, Row } from 'react-bootstrap';
import { Pencil, Plus, Save } from 'react-bootstrap-icons';
import { notify } from '../notify/notify';

interface AdminUsersProps extends CommonProperties {}
export default function AdminUsers(props: AdminUsersProps){
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()
  const [users, setUsers]: [CrystalBallRolesTableRow[], any] = useState([])
  const [roles, setRoles]: [CrystalBallRolesTableRow[], any] = useState([])
  const [refresh, setRefresh]: [string, any] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})
  const [displayNames, setDisplayNames] = useState({});
  const [newUserDisplayName, setNewUserDisplayName] = useState('')
  const [newUserEmail, setNewUserEmail] = useState('')

  const refreshScreen = () => {
    setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }

  useEffect(() => {
    const load = async function() {
      const response = await apiFetch<CrystalBallRolesTableRow[]>({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        params: [
          {key: 'table', value: 'crystalBallRoles'}
        ]
      })
      if(!response.ok){
        setError({
          closeError: () => setError({visible: false}),
          content: JSON.stringify(response.body),
          description: 'Failed to get users and roles from the crystalBallRoles table.',
          title: 'Loading Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      setUsers(response.body.filter(r => r.partitionKey === 'user'))
      setRoles(response.body.filter(r => r.partitionKey === 'role'))
      setDisplayNames(() => {
        const initial = {};
        for (const user of response.body.filter(r => r.partitionKey === 'user') ?? []) {
          initial[user.rowKey] = user.username;
        }
        return initial;
      })
    }
    load()
  }, [refresh])

  const handleUserRoleChecked = useCallback(async (e) => {
    const {userkey, rolekey} = e.currentTarget.dataset
    const checked = e.currentTarget.checked

    const role = roles.find(r => r.rowKey === rolekey)
    const user = users.find(u => u.rowKey === userkey)
    let newRoles = []
    if(checked === true){
      newRoles = [...JSON.parse(user.roles), role.rowKey]
    } else {
      for(const role of JSON.parse(user.roles)){
        if(role === rolekey){
          continue
        }
        newRoles.push(role)
      }
    }

    const response = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      body: JSON.stringify({
        table: 'crystalBallRoles',
        records: [{
          partitionKey: 'user',
          rowKey: user.rowKey,
          roles: JSON.stringify(newRoles)
        }]
      })
    })
    if(!response.ok){
      setError({
        closeError: () => setError({visible: false}),
        content: JSON.stringify(response.body),
        description: 'Failed to update user roles.',
        title: 'Update Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    notify('success', `${checked === true ? 'Added' : 'Removed'} ${role.title} for ${user.username} successfully!`)
    refreshScreen()
  }, [users, roles])

  const handleSaveUserDisplayName = useCallback(async (e) => {
    const {userkey} = e.currentTarget.dataset
    changeStep('Updating User Display Name')
    changeSubStep(`Updating to ${displayNames[userkey]}`)
    toggleLoading(true)
    const response = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      body: JSON.stringify({
        table: 'crystalBallRoles',
        records: [{
          partitionKey: 'user',
          rowKey: userkey,
          username: displayNames[userkey]
        }]
      })
    })
    if(!response.ok){
      setError({
        closeError: () => setError({visible: false}),
        content: JSON.stringify(response.body),
        description: 'Failed to update user display name.',
        title: 'Update Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    refreshScreen()
  }, [displayNames, users])

  const handleSaveNewUser = useCallback(async () => {
    if(
      !newUserDisplayName || 
      !newUserEmail ||
      newUserDisplayName.trim() === '' ||
      newUserEmail.trim() === ''
    ){
      return
    }

    const existingUser = users.find(u => decodeURIComponent(u.rowKey) === newUserEmail)
    if(existingUser){
      setError({
        closeError: () => setError({visible: false}),
        content: JSON.stringify(existingUser),
        description: 'User already exists with that email.',
        title: 'Create Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeStep('Creating New User')
    changeSubStep(`Creating user account for ${newUserDisplayName} with email ${newUserEmail}`)
    toggleLoading(true)
    const response = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      body: JSON.stringify({
        table: 'crystalBallRoles',
        records: [{
          partitionKey: 'user',
          rowKey: encodeURIComponent(newUserEmail),
          username: newUserDisplayName,
          roles: JSON.stringify(["new"])
        }]
      })
    })
    if(!response.ok){
      setError({
        closeError: () => setError({visible: false}),
        content: JSON.stringify(response.body),
        description: 'Failed to create a new user.',
        title: 'Create Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    setNewUserDisplayName('')
    setNewUserEmail('')
    toggleLoading(false)
    notify('success', 'User created successfully!')
    refreshScreen()
  }, [newUserDisplayName, newUserEmail, users])

  const view = useMemo(() => {
    const rows = []
    const sortedUsers = users.sort((a, b) => a.username.localeCompare(b.username));
    for(const user of sortedUsers){
      const userAssignedRoles: string[] = JSON.parse(user.roles)
      rows.push(<tr>
        <td style={{width: '7%'}}>
          <Form.Control 
            id={`u-${user.rowKey}`}
            style={{fontWeight: 'lighter'}} 
            value={displayNames[user.rowKey] || ''} // Controlled component
            onChange={(e) => setDisplayNames(prev => ({
              ...prev,
              [user.rowKey]: e.target.value
            }))}
          /> 
          <span title={`Save the user's display name.`} style={{float: 'right'}}><Button data-userkey={user.rowKey} onClick={handleSaveUserDisplayName} variant='secondary'>SAVE</Button></span> 
          {userAssignedRoles.includes('admin') && <><span style={{float: 'left'}} className='beacon-label'>ADMIN</span></>}
        </td>
        <td style={{width: '5%'}}>{decodeURIComponent(user.rowKey)}</td>
        <td>
          <Accordion className='custom-accordion'>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>
                <span className='beacon-header'>ROLES</span>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  {roles.map((r, idx) => (
                    <Col key={`${user.rowKey}-${r.rowKey}`} xs={6} md={3} lg={2} className="mb-2 d-flex align-items-center">
                      <Form.Check 
                        onChange={handleUserRoleChecked}
                        data-userkey={user.rowKey}
                        data-rolekey={r.rowKey}
                        id={`role-${user.rowKey}-${idx}`} 
                        title={r.description}
                        className="me-2"
                        defaultChecked={userAssignedRoles.includes(r.rowKey)}
                        style={{
                          width: '1.2em',
                          height: '1.2em',
                          borderRadius: '4px',
                        }}
                      />
                      <label 
                        htmlFor={`role-${user.rowKey}-${idx}`} 
                        className="mb-0" 
                        style={{ fontSize: '1.2em', cursor: 'pointer' }}
                        title={r.description}
                      >
                        {r.title}
                      </label>&nbsp;
                    </Col>
                  ))}
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </td>
      </tr>)
    }
    return <div>
      <div id='top'></div>
      <div className='beacon-card'>
        <table className='beacon-table'>
          <tbody>
            <tr>
              <td><span className='beacon-header'>Administer Users</span></td>
            </tr>
            <tr>
              <td><span className='beacon-label'>Modify user roles and display name or create a new user record.</span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <br/>
      <CustomError 
        closeError={() => {setError({visible: false})}}
        content={error?.content}
        description={error?.description}
        title={error?.title}
        visible={error?.visible}
      />
      <Accordion className='custom-accordion'>
        <Accordion.Item eventKey='0'>
          <Accordion.Header>
            <Plus size='30'/><span className='beacon-header'>NEW USER</span>
          </Accordion.Header>
          <Accordion.Body>
            <span className='beacon-label'>The user must have a Serenitize account already created in Azure or they will receive an error when attempting to login. They must also be a part of our Developer group in Azure RBAC.</span>
            <table className='admin-table'>
              <thead>
                <tr className="sticky-row-2">
                  <th>DISPLAY</th>
                  <th>EMAIL</th>
                  <th>ROLES</th>
                </tr>
              </thead>
              <tbody>
                <td style={{width: '7%'}}>
                  <Form.Control value={newUserDisplayName} onChange={(e) => {setNewUserDisplayName(e.target.value)}} style={{fontWeight: 'lighter'}} placeholder='Player'/>
                </td>
                <td style={{width: '15%'}}>
                  <Form.Control value={newUserEmail} onChange={(e) => {setNewUserEmail(e.target.value)}} style={{fontWeight: 'lighter'}} placeholder='a-new-player-has-joined@serenitize.com'/>
                </td>
                <td>
                  <span className='beacon-label'>ROLES ASSIGNED AFTER CREATION...</span>
                </td>
              </tbody>
            </table>
            <br/>
            <Button variant='success' onClick={handleSaveNewUser}><Save /> &nbsp;<span className='beacon-header'>SAVE</span></Button>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <br/>
      <div className='beacon-card'>
        <span style={{cursor: 'pointer'}} title='Refresh the table.' onClick={() => {refreshScreen()}}><RefreshSvg height={20} /></span><span className='beacon-label'> as of {refresh}</span>
        <span className='beacon-label'> with {rows.length} users(s) loaded.</span>
      </div>
      <br/>
      <div className='beacon-card'>
        <table className='admin-table'>
          <thead>
            <tr className="sticky-row-2">
              <th>DISPLAY</th>
              <th>EMAIL</th>
              <th>ROLES</th>
            </tr>
          </thead>
          <tbody>
            {rows}
          </tbody>
        </table>
      </div>
    </div>
  }, [users, roles, refresh, displayNames, newUserDisplayName, newUserEmail, error])

  return view
}